/**
 * Web Push subscription helper — funziona su Android/Chrome/Firefox e su
 * iOS 16.4+ (solo quando la PWA è installata a Home).
 *
 * Uso:
 *   import { requestPushPermission, unsubscribePush, getPushState } from "@/lib/push";
 *   const state = await getPushState();  // { supported, permission, subscribed, iosNeedsInstall }
 *   await requestPushPermission();       // ritorna { ok: boolean, reason?: string }
 */
import api from "@/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

function isIOSSafari() {
  const ua = navigator.userAgent || "";
  const isIOS = /iP(hone|ad|od)/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS/.test(ua);
  return isIOS && isSafari;
}

function isPWAInstalled() {
  return (
    window.matchMedia && window.matchMedia("(display-mode: standalone)").matches
  ) || window.navigator.standalone === true;
}

export async function getPushState() {
  const supported =
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

  const state = {
    supported,
    permission: supported ? Notification.permission : "unsupported",
    subscribed: false,
    iosNeedsInstall: false,
  };

  if (!supported) {
    if (isIOSSafari() && !isPWAInstalled()) {
      state.iosNeedsInstall = true;
    }
    return state;
  }

  // Su iOS Safari, le push richiedono che la PWA sia installata a Home
  if (isIOSSafari() && !isPWAInstalled()) {
    state.iosNeedsInstall = true;
  }

  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (reg) {
      const sub = await reg.pushManager.getSubscription();
      state.subscribed = !!sub;
    }
  } catch (e) {
    // no-op
  }
  return state;
}

async function fetchVapidKey() {
  const { data } = await api.get("/push/vapid-public-key");
  return data.public_key;
}

async function ensureSWReady() {
  if (!("serviceWorker" in navigator)) throw new Error("Service Worker non supportato");
  let reg = await navigator.serviceWorker.getRegistration();
  if (!reg) reg = await navigator.serviceWorker.register("/service-worker.js");
  await navigator.serviceWorker.ready;
  return reg;
}

export async function requestPushPermission() {
  if (!("Notification" in window) || !("serviceWorker" in navigator)) {
    return { ok: false, reason: "unsupported" };
  }
  if (isIOSSafari() && !isPWAInstalled()) {
    return { ok: false, reason: "ios-needs-install" };
  }

  let perm = Notification.permission;
  if (perm === "default") {
    perm = await Notification.requestPermission();
  }
  if (perm !== "granted") return { ok: false, reason: "denied" };

  const reg = await ensureSWReady();
  const publicKey = await fetchVapidKey();

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  const payload = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: payload.endpoint,
    keys: payload.keys,
    expirationTime: payload.expirationTime || null,
  });

  return { ok: true };
}

export async function unsubscribePush() {
  if (!("serviceWorker" in navigator)) return { ok: false };
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return { ok: false };
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true };
  try {
    await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
  } catch (_) {}
  await sub.unsubscribe();
  return { ok: true };
}
